import { useEffect, useRef } from 'react';
import clsx from 'clsx';

interface Props {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  // A coloured mark beside the title: the rhythm's colour.
  accent?: string;
  badge?: React.ReactNode;
  onClose: () => void;
  onBack?: () => void;
  width?: number;
  footer?: React.ReactNode;
  children: React.ReactNode;
  bodyClassName?: string;
}

// The Plan page's dialog: a centred card over a blurred backdrop that eases
// in, with a header, a scrolling body and a pinned footer for actions.
export default function Modal({
  title,
  subtitle,
  accent,
  badge,
  onClose,
  onBack,
  width = 520,
  footer,
  children,
  bodyClassName
}: Props) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      // Only the topmost dialog closes.
      const all = document.querySelectorAll('[data-plan-modal]');
      if (all[all.length - 1] !== cardRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const el = cardRef.current?.querySelector<HTMLElement>('[data-autofocus]');
    el?.focus();
  }, []);

  return (
    <div
      className="plan-backdrop fixed inset-0 z-50 flex items-center justify-center p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={cardRef}
        data-plan-modal
        role="dialog"
        aria-modal="true"
        className="plan-modal flex max-h-[min(86dvh,760px)] w-full flex-col overflow-hidden rounded-xl border border-[var(--border-strong)] bg-[var(--popover-bg)] shadow-[var(--shadow-md)]"
        style={{ maxWidth: width }}
      >
        <header className="flex items-start gap-3 px-5 pb-3 pt-4">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label="back"
              className="-ml-2 mt-[1px] grid h-7 w-7 place-items-center rounded-md text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
            >
              ‹
            </button>
          )}
          {accent && (
            <span
              className="mt-[5px] h-[14px] w-[14px] flex-shrink-0 rounded-[4px]"
              style={{ background: accent }}
              aria-hidden
            />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="m-0 text-[1.02rem] font-semibold leading-snug">{title}</h2>
              {badge}
            </div>
            {subtitle && (
              <div className="mt-[2px] text-[0.83rem] text-[var(--text-muted)]">{subtitle}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="close"
            className="-mr-2 grid h-7 w-7 flex-shrink-0 place-items-center rounded-md text-[1.1rem] leading-none text-[var(--text-faint)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
          >
            ×
          </button>
        </header>
        <div className={clsx('min-h-0 flex-1 overflow-y-auto px-5 pb-4', bodyClassName)}>
          {children}
        </div>
        {footer && (
          <footer className="flex flex-wrap items-center gap-2 border-t border-[var(--border)] bg-[color-mix(in_srgb,var(--popover-bg)_70%,var(--bg))] px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

export function Button({
  variant = 'ghost',
  className,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'outline' | 'danger';
}) {
  return (
    <button
      type="button"
      {...rest}
      className={clsx(
        'inline-flex items-center justify-center gap-[6px] rounded-md px-3 py-[6px] text-[0.84rem] transition-[background,opacity,transform] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50',
        variant === 'primary' && 'bg-[var(--accent)] font-medium text-[var(--accent-fg)] hover:opacity-90',
        variant === 'ghost' &&
          'text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]',
        variant === 'outline' &&
          'border border-[var(--border-strong)] text-[var(--text)] hover:bg-[var(--bg-hover)]',
        variant === 'danger' && 'text-[var(--today-fg)] hover:bg-[var(--today-bg)]',
        className
      )}
    />
  );
}

export function Pill({ children, tone = 'muted' }: { children: React.ReactNode; tone?: 'muted' | 'accent' | 'warn' }) {
  return (
    <span
      className={clsx(
        'rounded-full px-2 py-[1px] text-[0.7rem] font-medium uppercase tracking-wide',
        tone === 'muted' && 'bg-[var(--bg-hover)] text-[var(--text-muted)]',
        tone === 'accent' && 'bg-[color-mix(in_srgb,var(--accent)_18%,transparent)] text-[var(--accent)]',
        tone === 'warn' && 'bg-[var(--today-bg)] text-[var(--today-fg)]'
      )}
    >
      {children}
    </span>
  );
}

export function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="flex flex-col gap-[6px]">
      <span className="text-[0.72rem] font-medium uppercase tracking-wide text-[var(--text-faint)]">
        {label}
      </span>
      {children}
      {hint && <span className="text-[0.75rem] text-[var(--text-faint)]">{hint}</span>}
    </label>
  );
}

export const inputClass =
  'rounded-md border border-[var(--border-strong)] bg-[var(--bg)] px-[10px] py-[6px] text-[0.88rem] text-[var(--text)] outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[color-mix(in_srgb,var(--accent)_25%,transparent)]';

export function Segmented<T extends string>({
  value,
  options,
  onChange
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded-md bg-[var(--bg-muted)] p-[2px]">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={clsx(
            'rounded px-3 py-[5px] text-[0.8rem] transition-colors',
            value === o.value
              ? 'bg-[var(--bg)] text-[var(--text)] shadow-sm'
              : 'text-[var(--text-muted)] hover:text-[var(--text)]'
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Toggle({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex items-center justify-between gap-3 rounded-md py-[6px] text-left text-[0.88rem]"
    >
      <span>{label}</span>
      <span
        className={clsx(
          'relative h-[20px] w-[34px] flex-shrink-0 rounded-full transition-colors',
          checked ? 'bg-[var(--accent)]' : 'bg-[var(--border-strong)]'
        )}
      >
        <span
          className={clsx(
            'absolute top-[2px] h-[16px] w-[16px] rounded-full bg-white shadow transition-[left]',
            checked ? 'left-[16px]' : 'left-[2px]'
          )}
        />
      </span>
    </button>
  );
}

export function Check({
  checked,
  onChange,
  color
}: {
  checked: boolean;
  onChange: () => void;
  color?: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={onChange}
      className={clsx(
        'grid h-[18px] w-[18px] flex-shrink-0 place-items-center rounded-[5px] border-[1.5px] transition-colors',
        checked ? 'border-transparent' : 'border-[var(--border-strong)] hover:border-[var(--text-muted)]'
      )}
      style={checked ? { background: color ?? 'var(--accent)' } : undefined}
    >
      {checked && (
        <svg viewBox="0 0 12 12" width="11" height="11" aria-hidden>
          <path d="M2.5 6.2 5 8.5l4.5-5" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}
